import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line } from 'react-chartjs-2';
import { chartColors } from '@/utils/chartColors';
import { useRsColors } from '@/hooks/useRsColors';
import { getDateXAxisConfig, getSmartYAxisConfig, isDateLabel, parseDateLiteral, formatDateLabel, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { getChartFontSize } from '@/utils/chartTypography';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import useChartWindow from '@/hooks/useChartWindow';
import { getDefaultPreviousYearWindowFromLabels, sliceLabelsAndDatasets } from '@/utils/chartWindow';
import { shouldShowDataLabelsByFrequency } from '@/utils/chartDataLabelPolicy';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

export interface LineDataset {
  data: (number | null)[];
  label: string;
  color?: string;
}

interface PolymerLineChartProps {
  datasets: LineDataset[];
  labels: string[];
  yLabel?: string;
  className?: string;
  compact?: boolean;
  showDataLabels?: boolean;
  dataLabelMaxWindowDays?: number;
  alwaysShowDataLabels?: boolean;
  dataLabelDecimals?: number;
  rightPadding?: number;
  showRangeSlider?: boolean;
  defaultVisiblePoints?: number;
  /** Use the inventory-change axis rule: <3yr → 6-month MMM yyyy, >3yr → year only + hide value labels */
  threeYearDateLabels?: boolean;
}

const PolymerLineChart: React.FC<PolymerLineChartProps> = ({
  datasets,
  labels,
  yLabel,
  className,
  compact = false,
  showDataLabels = false,
  dataLabelMaxWindowDays = 365,
  alwaysShowDataLabels = false,
  dataLabelDecimals = 3,
  rightPadding = 0,
  showRangeSlider = false,
  defaultVisiblePoints,
  threeYearDateLabels = false,
}) => {
  const rs = useRsColors();
  const defaultDateWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(labels),
    [labels],
  );

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    preferredVisiblePoints: defaultVisiblePoints,
    defaultStartIndex: defaultVisiblePoints ? undefined : defaultDateWindow.startIndex,
    defaultEndIndex: defaultVisiblePoints ? undefined : defaultDateWindow.endIndex,
  });

  const visibleData = useMemo<{ labels: string[]; datasets: LineDataset[] }>(
    () => (showRangeSlider
      ? sliceLabelsAndDatasets(labels, datasets, startIndex, endIndex)
      : { labels, datasets }),
    [showRangeSlider, labels, datasets, startIndex, endIndex],
  );

  const shouldShowDataLabels = useMemo(
    () => shouldShowDataLabelsByFrequency({
      enabled: showDataLabels,
      labels: visibleData.labels,
      alwaysShow: alwaysShowDataLabels,
      maxDailyWindowDays: dataLabelMaxWindowDays,
    }),
    [showDataLabels, visibleData.labels, alwaysShowDataLabels, dataLabelMaxWindowDays],
  );

  const isDateAxis = visibleData.labels.length > 0 && isDateLabel(visibleData.labels[0]);
  const hasRepeatedAdjacentLabels = !isDateAxis && visibleData.labels.some((label, index) => index > 0 && label === visibleData.labels[index - 1]);

  const chartDatasets = visibleData.datasets.map((ds, i) => {
    const color = ds.color || chartColors.getColorByIndex(i);
    return {
      label: ds.label,
      data: ds.data,
      borderColor: color,
      backgroundColor: `${color}1a`,
      pointRadius: compact ? 0 : 2,
      pointHoverRadius: compact ? 3 : 4,
      borderWidth: 3,
      tension: 0.3,
      spanGaps: true,
    };
  });

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        right: Math.max(rightPadding, 12),
        bottom: 8,
        left: 8,
      },
    },
    plugins: {
      legend: {
        display: datasets.length > 1,
        labels: {
          color: rs.offWhite || '#f3f4f6',
          usePointStyle: true,
          pointStyle: 'line',
          font: { size: getChartFontSize() },
        },
      },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            const raw = items[0].label;
            if (isDateLabel(raw)) return formatDateLabel(parseDateLiteral(raw));
            return raw;
          },
        },
      },
      datalabels: {
        // When using the 3-year date rule, also hide all labels once dragged past the 3-year mark
        display: threeYearDateLabels
          ? shouldShowDataLabels && isWithinThreeYearRange(visibleData.labels)
          : shouldShowDataLabels,
        color: rs.offWhite || '#f3f4f6',
        align: 'top',
        anchor: 'end',
        font: { size: getChartFontSize() },
        formatter: (value: number | null) => {
          if (value === null || value === undefined) return '';
          return Number(value).toFixed(dataLabelDecimals);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          ...(threeYearDateLabels
            ? {
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false,
                callback: (_value: any, index: number) => {
                  const lbl = visibleData.labels[index];
                  const d = parseDateLiteral(String(lbl));
                  // <3yr window: every 6 months (MMM yyyy); dragged past 3yr: year only
                  if (!isNaN(d.getTime())) {
                    return inventoryChangeAxisLabel(d, isPastThreeYearMark(visibleData.labels)) || null;
                  }
                  return lbl;
                },
              }
            : isDateAxis
            ? getDateXAxisConfig(visibleData.labels)
            : hasRepeatedAdjacentLabels
            ? {
                autoSkip: true,
                maxTicksLimit: Math.min(8, Math.max(4, Math.ceil(visibleData.labels.length / 4))),
                maxRotation: 0,
                callback: (_value: any, index: number) => visibleData.labels[index],
              }
            : {}),
        },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
        },
        grid: { color: rs.gridLine },
        ...(yLabel ? { title: { display: true, text: yLabel, color: rs.offWhite || '#f3f4f6', font: { size: getChartFontSize() } } } : {}),
        ...getSmartYAxisConfig(visibleData.datasets.flatMap((ds: any) => (ds.data as (number | null)[]).filter((v): v is number => v != null))),
      },
    },
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs]);

  return (
    <div className={className || 'w-full h-full bg-rs-jet rounded'} data-testid="polymer-line-chart">
      <div className={showRangeSlider && hasWindowing ? 'h-[calc(100%-50px)] w-full' : 'h-full w-full'}>
        <Line key={rs.offWhite} data={{ labels: visibleData.labels, datasets: chartDatasets }} options={options} plugins={[hoverLinePlugin]} />
      </div>
      {showRangeSlider && hasWindowing && (
        <ChartRangeSlider
          totalPoints={labels.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onChange={setWindow}
        />
      )}
    </div>
  );
};

export default PolymerLineChart;
